import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { api, setToken, getToken } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setAuthLoading(false);
      return;
    }
    try {
      const res = await api.get('/api/auth/me');
      if (res.success && res.data?.user) {
        setUser(res.data.user);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(res.data.user));
      }
    } catch (err) {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('user');
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = useCallback((userData, token) => {
    if (token) setToken(token);
    setUser(userData);
    setIsAuthenticated(true);
    try {
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    try {
      localStorage.removeItem('user');
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
  }, []);

  const register = useCallback((userData, token) => {
    if (token) setToken(token);
    setUser(userData);
    setIsAuthenticated(true);
    try {
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
  }, []);

  const updateCurrentUser = useCallback((userData) => {
    setUser(userData);
    try {
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      authLoading,
      login,
      logout,
      register,
      updateCurrentUser,
    }),
    [user, isAuthenticated, authLoading, login, logout, register, updateCurrentUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
